import React, { useState, useEffect } from "react";
import { Globe, Lock, Users, Info, ChevronDown } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";

const BasicInformation = ({ formData, handleInputChange, handleMaxAttendeesChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleCategorySelect = (categoryName) => {
    handleInputChange({
      target: {
        name: 'category',
        value: categoryName
      }
    });
    setShowCategoryDropdown(false);
  };

  const selectedCategory = categories.find(cat => cat.categoryName === formData.category);

  return (
    <div className="space-y-4">
      {/* Title Input */}
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <input
          type="text"
          name="title"
          placeholder="Activity Title"
          value={formData.title}
          onChange={handleInputChange}
          className="w-full text-lg font-medium placeholder:text-gray-400 focus:outline-none"
          maxLength={100}
        />
      </div>

      {/* Category Selector */}
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              {selectedCategory && (
                <img 
                  src={selectedCategory.categoryIcon} 
                  alt={selectedCategory.categoryName}
                  className="w-6 h-6 object-contain"
                />
              )}
              <div>
                <p className="text-sm text-gray-600">Category</p>
                <p className="font-medium">
                  {selectedCategory ? selectedCategory.categoryName : "Select a category"}
                </p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
              showCategoryDropdown ? 'transform rotate-180' : ''
            }`} />
          </button>

          {showCategoryDropdown && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg py-2 z-50">
              {loading ? (
                <div className="px-4 py-2 text-gray-500">Loading categories...</div>
              ) : categories.length > 0 ? (
                categories.map(category => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategorySelect(category.categoryName)}
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                  >
                    <img 
                      src={category.categoryIcon} 
                      alt={category.categoryName}
                      className="w-6 h-6 object-contain"
                    />
                    <span>{category.categoryName}</span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500">No categories available</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Privacy Toggle */}
      <div className="bg-white p-4 rounded-xl shadow-sm space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {formData.isPublic ? (
              <Globe className="w-5 h-5 text-green-500" />
            ) : (
              <Lock className="w-5 h-5 text-blue-500" />
            )}
            <span className="font-medium">
              {formData.isPublic ? "Public Activity" : "Private Activity"}
            </span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="isPublic"
              className="sr-only peer"
              checked={formData.isPublic}
              onChange={handleInputChange}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
          </label>
        </div>
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            {formData.isPublic 
              ? "Anyone can find and join this activity" 
              : "Only people you invite can join this activity"}
          </p>
        </div>
      </div>

      {/* Maximum Attendees */}
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <Users className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-600">Maximum attendees</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleMaxAttendeesChange(-1)}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 text-lg font-medium"
          >
            -
          </button>
          <input
            type="number"
            name="maxAttendees"
            value={formData.maxAttendees}
            onChange={handleInputChange}
            className="flex-1 text-center focus:outline-none font-medium"
            min="1"
            max="1000"
          />
          <button
            type="button"
            onClick={() => handleMaxAttendeesChange(1)}
            className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 text-lg font-medium"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;